/*
  Formful's app block renders a separate <label> sitting above each
  field, not inline placeholder text — that's why it "floats" instead
  of sitting inside the field like the rest of the site's dw-input
  fields (which use the native placeholder attribute, no visible
  label).

  This copies each field's label text into its input/textarea's
  placeholder attribute, then visually hides the original <label>
  (kept in the DOM, off-screen, so it still exists for screen readers
  — same trade-off the rest of the site's dw-input fields already make
  today, since those also rely on placeholder text with no separate
  label).

  Formful's form renders asynchronously after its own script loads, so
  a MutationObserver watches for field-wrappers being inserted rather
  than only running once on DOMContentLoaded.
*/
(function () {
  function enhanceField(wrapper) {
    if (wrapper.dataset.dwLabelMoved) return;

    var label = wrapper.querySelector('.formful__label');
    var field = wrapper.querySelector('input.formful__input, textarea.formful__input');
    if (!label || !field) return;

    var text = label.textContent.trim();
    if (text) {
      field.setAttribute('placeholder', text);
    }

    label.classList.add('dw-visually-hidden');
    wrapper.dataset.dwLabelMoved = 'true';
  }

  function scan(root) {
    var nodes = root.querySelectorAll
      ? root.querySelectorAll('.formful__field-wrapper')
      : [];
    nodes.forEach(enhanceField);
  }

  /*
    2026-07-22: added because there's no edit control for this message
    in Formful's own app admin (confirmed by the user — it only offers a
    "view" option for the success message, nothing editable). Since
    Formful renders this text as plain HTML we already have full access
    to, overriding it here sidesteps that limitation entirely instead of
    waiting on Formful to add one.

    TO CHANGE THE WORDING: edit the strings in the array below. Each
    entry becomes its own paragraph. Set CUSTOM_THANKS_MESSAGE_HTML to
    null (instead of an array) to stop overriding it and fall back to
    whatever Formful renders on its own.
  */
  var CUSTOM_THANKS_MESSAGE_HTML = [
    'Thank you for submitting your form!',
    'Our team will do our best to respond promptly.'
  ];

  function applyCustomMessage(msg) {
    if (!CUSTOM_THANKS_MESSAGE_HTML || msg.dataset.dwMessageOverridden) return;
    msg.innerHTML = CUSTOM_THANKS_MESSAGE_HTML.map(function (line) {
      return '<p>' + line + '</p>';
    }).join('');
    msg.dataset.dwMessageOverridden = 'true';
  }

  /*
    2026-07-22: on successful submit, Formful doesn't remove/replace the
    form — it just toggles a `formful__hide` class on the pre-existing
    .formful__thanks-message div (confirmed live: the div sits in the DOM
    from page load, hidden by that class). The heading/paragraph/email
    above the form and the form itself stay visible the whole time, which
    is why the confirmation reads as cluttered rather than a clean
    standalone message.

    This watches that one class for changes (a plain childList/subtree
    observer won't catch class-attribute toggles, hence the separate
    attribute observer) and, the moment the message becomes visible:
      - hides the contact header (heading/paragraph/email) and the form
      - after 10s, reverses it: re-hides the message, restores the header
        and form so the page is back to its normal pre-submit state.
    The wording itself is overridden by applyCustomMessage() above —
    see CUSTOM_THANKS_MESSAGE_HTML if it needs to change.
  */
  function watchThanksMessage(msg) {
    if (msg.dataset.dwThanksWatched) return;
    msg.dataset.dwThanksWatched = 'true';

    applyCustomMessage(msg);

    var hideTimer = null;

    /*
      2026-07-22: this was the actual bug, confirmed from a real live
      submission (checked the DOM directly): Formful does NOT remove
      formful__hide on success like the class name implies. It leaves
      formful__hide in place and adds a SECOND class, formful__show,
      alongside it — the real post-submit class list is
      "formful__thanks-message formful__hide formful__show". Whatever
      CSS rule formful__show carries wins out and forces it visible
      (confirmed opacity:1 with both classes present). isVisible() was
      checking for formful__hide's ABSENCE, which never happens — hide
      never goes away, so this always evaluated false and
      showMessageOnly() never ran. The real signal is formful__show's
      presence, not formful__hide's absence.
    */
    function isVisible() {
      return msg.classList.contains('formful__show');
    }

    /*
      Re-query header/form fresh every time rather than caching them once
      — confirmed live that Formful's own app can swap out the
      .formful__form DOM node (its internal reactivity re-renders it) any
      time after the page first loads, which silently detaches a cached
      reference and would leave the newly-rendered form untouched by
      classList calls made against the stale node.
    */
    var FADE_MS = 200; // keep in sync with the transition duration in dw-contact.css

    /*
      display:none can't be transitioned (a property either participates
      in layout or it doesn't — there's no partial state to animate), so
      this fades opacity to 0 first via the dw-contact--fading class
      (animated by the `transition: opacity` rule in dw-contact.css),
      then only once that's finished does it add dw-contact--hidden to
      actually pull the element out of layout. Skipping straight to
      dw-contact--hidden would cut it instantly with no visible fade.
    */
    function fadeOutThenHide(el) {
      if (!el) return;
      el.classList.add('dw-contact--fading');
      window.setTimeout(function () {
        el.classList.add('dw-contact--hidden');
      }, FADE_MS);
    }

    /*
      Reverse order from fadeOutThenHide: remove dw-contact--hidden first
      so the element re-enters layout (still at opacity 0 from
      dw-contact--fading), force a style recalculation with the
      offsetHeight read, THEN remove dw-contact--fading. Without that
      forced recalculation the browser can collapse both class changes
      into a single paint and the element would just pop straight to
      opacity 1 with no visible fade-in.
    */
    function fadeIn(el) {
      if (!el) return;
      el.classList.remove('dw-contact--hidden');
      void el.offsetHeight;
      el.classList.remove('dw-contact--fading');
    }

    function showMessageOnly() {
      var header = document.querySelector('.dw-contact__header');
      var form = document.querySelector('.formful__form');
      fadeOutThenHide(header);
      fadeOutThenHide(form);
    }

    function restoreForm() {
      msg.classList.remove('formful__show'); // fades out on its own via .formful__thanks-message's transition
      var header = document.querySelector('.dw-contact__header');
      var form = document.querySelector('.formful__form');
      fadeIn(header);
      if (form) {
        /*
          2026-07-22: confirmed live on a real post-submit page — Formful
          adds its OWN formful__hide class straight onto .formful__form
          on a real successful submit (separate from formful__show on
          the message, and separate from anything we add). It's Formful's
          own built-in "don't let them resubmit" behavior. We were only
          ever clearing our own dw-contact--hidden class, so the form
          stayed invisible under Formful's class the whole time — once
          Formful's own message fade finished on its own, both the
          message and the form ended up hidden with nothing left visible.
          Stripping formful__hide here too (before the fade-in, so it
          isn't fighting our own opacity transition) is what actually
          brings the fields back.
        */
        form.classList.remove('formful__hide');
        /*
          2026-07-22: we only ever toggled visibility classes on the
          SAME form element that was just submitted — we never actually
          cleared it, so whatever the visitor typed (and any file picked
          in the upload field) was still sitting in the fields the whole
          10s later, just hidden. form.reset() is the native browser
          call for "put every field in this form back to its blank
          starting state" (text/textarea back to empty, file picker
          cleared, etc.) — called here, before fadeIn(form) reveals it
          again, so the reset happens while it's still invisible and
          what the next visitor sees is a genuinely empty form, not the
          previous submission's leftovers.
        */
        form.reset();
      }
      fadeIn(form);
    }

    function handleVisibilityChange() {
      if (isVisible()) {
        showMessageOnly();
        clearTimeout(hideTimer);
        hideTimer = setTimeout(restoreForm, 5000);
      }
    }

    var classObserver = new MutationObserver(handleVisibilityChange);
    classObserver.observe(msg, { attributes: true, attributeFilter: ['class'] });

    /*
      2026-07-22: this was the actual bug behind "contact info doesn't
      disappear on submit." The check above only reacts to the class
      CHANGING on this node going forward — but confirmed live that a
      real submit can have Formful swap in a brand-new
      .formful__thanks-message element that's already visible (no
      formful__hide class) from the moment it's inserted, rather than
      flipping the class on the node that was already on the page. A
      node that's born visible never fires a class-attribute mutation,
      so the observer above never ran and the header/form never hid.
      Running the same check once immediately, right after attaching
      the observer, covers that case too.
    */
    handleVisibilityChange();

    /*
      Belt-and-suspenders for the same re-render behavior: if Formful
      swaps in a fresh .formful__form node WHILE the message is still
      showing (the 10s window), that new node won't have
      dw-contact--hidden on it yet since showMessageOnly() already ran
      against the old node. Watch the wrapper for that and re-hide
      immediately so the form can't flash back into view mid-window.
    */
    var wrapper = msg.closest('.formful__wrapper') || document.body;
    var formSwapObserver = new MutationObserver(function (mutations) {
      if (!isVisible()) return;
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('.formful__form')) {
            node.classList.add('dw-contact--hidden');
          }
        });
      });
    });
    formSwapObserver.observe(wrapper, { childList: true, subtree: true });
  }

  function scanThanksMessage(root) {
    var nodes = root.querySelectorAll
      ? root.querySelectorAll('.formful__thanks-message')
      : [];
    nodes.forEach(watchThanksMessage);
  }

  scan(document);
  scanThanksMessage(document);

  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches('.formful__field-wrapper')) {
          enhanceField(node);
        }
        if (node.matches && node.matches('.formful__thanks-message')) {
          watchThanksMessage(node);
        }
        if (node.querySelectorAll) {
          scan(node);
          scanThanksMessage(node);
        }
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();